import { makeAutoObservable } from "mobx";
import { getEnv } from "mobx-easy";
import { DBAction } from "../../../enum/DBAction";
import { StoreState } from "../../../enum/StoreState";
import { RootEnv } from "../../setup/create-store";
import {
  IOperationRuleLocation,
  IUpdateOperationRuleLocationOrderParams,
} from "./OperationRuleLocationModel";
import i18n from "../../../i18n";
import { LocationFormValues } from "pages/OceanWeatherIntelligence/OperationRuleLocation/types";

export default class OperationRuleLocationStore {
  operationRuleLocation: IOperationRuleLocation | undefined;

  operationRuleLocations: IOperationRuleLocation[] = [];

  operationRuleLocationModalVisible: boolean = false;

  operationRuleLocationAction: DBAction = DBAction.CREATE;

  operationRuleLocationMainId: number = 0;

  state: string = StoreState.DONE;

  messageError: string = "";

  actionCallInsightsList: boolean = false;

  hasLocations: boolean = true;

  createFormValues: LocationFormValues | null = null;

  // Form values for editing location from Event Manager
  editLocationFormValues: {
    id?: number;
    name?: string;
    dashboardId?: number;
  } | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setOperationRuleLocation(value: IOperationRuleLocation | undefined) {
    this.operationRuleLocation = value;
  }

  setOperationRuleLocations(values: IOperationRuleLocation[]) {
    this.operationRuleLocations = values;
  }

  setOperationRuleLocationModalVisible(value: boolean) {
    this.operationRuleLocationModalVisible = value;
  }

  setOperationRuleLocationAction(value: DBAction) {
    this.operationRuleLocationAction = value;
  }

  setOperationRuleLocationMainId(value: number) {
    this.operationRuleLocationMainId = value;
  }

  setState(value: StoreState) {
    this.state = value;
  }

  setMessageError(value: string) {
    this.messageError = value;
  }

  setActionCallInsightsList() {
    this.actionCallInsightsList = !this.actionCallInsightsList;
  }

  setHasLocations(value: boolean) {
    this.hasLocations = value;
  }

  setCreateFormValues(value: LocationFormValues | null) {
    this.createFormValues = value;
  }

  setEditLocationFormValues(value: { id?: number; name?: string; dashboardId?: number } | null) {
    this.editLocationFormValues = value;
  }

  getOperationRuleLocation(id: number) {
    return this.operationRuleLocations.find(
      (operationRuleLocation) => operationRuleLocation.id === id
    ) as IOperationRuleLocation;
  }

  reset() {
    this.setState(StoreState.DONE);
    this.setOperationRuleLocationMainId(0);
    this.setOperationRuleLocations([]);
    this.setCreateFormValues(null);
  }

  async getOperationRuleLocationsApi(operationRuleId: number) {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    this.setState(StoreState.PEDDING);
    this.setOperationRuleLocations([]);
    this.setOperationRuleLocationMainId(0);
    try {
      const operationRuleLocations: IOperationRuleLocation[] =
        await operationRuleLocationService.getOperationRuleLocation({
          operationRuleId,
          skipAreaLocations: false,
        });

      if (operationRuleLocations.length > 0) {
        this.setOperationRuleLocationMainId(operationRuleLocations[0].id);
        const hasAnyOrder = operationRuleLocations.some(
          (x) => x.order !== null && x.order !== undefined
        );

        if (hasAnyOrder) {
          const orderedLocations = [...operationRuleLocations].sort(
            (a, b) => (a?.order ?? Infinity) - (b?.order ?? Infinity)
          );

          this.setOperationRuleLocations(orderedLocations);
        } else {
          this.setOperationRuleLocations(operationRuleLocations);
        }

        this.setHasLocations(true);
        this.setState(StoreState.DONE);
        return operationRuleLocations;
      } else {
        this.setOperationRuleLocationMainId(0);
        this.setOperationRuleLocations([]);
        this.setHasLocations(false);
      }
      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorGetLocationData"
        )
      );
    }
  }

  async getOperationRuleLocationById(operationRuleId: number, id: number) {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    let operationRuleLocation = undefined;
    try {
      operationRuleLocation =
        await operationRuleLocationService.getOperationRuleLocation({
          operationRuleId,
          id,
          skipAreaLocations: false,
        });
      if (operationRuleLocation) {
        this.setOperationRuleLocationMainId(id);
        this.setOperationRuleLocation(operationRuleLocation);
        this.setState(StoreState.DONE);
      } else {
        this.setState(StoreState.ERROR);
        this.setMessageError(
          i18n.t(
            "oceanWeatherIntelligence.operationRuleLocation.errorGetLocationData"
          )
        );
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorGetLocationData"
        )
      );
    }
  }

  async addOperationRuleLocation() {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    if (!this.operationRuleLocation) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorAddLocationData"
        )
      );
      return;
    }

    const isDrawnAreaType = this.operationRuleLocation.geofence && this.operationRuleLocation.geofence.length > 0;
    const isSelectedAreaType = this.operationRuleLocation.locationId && this.operationRuleLocation.locationId > 0;
    const isPointType = this.operationRuleLocation.macroRegionId && this.operationRuleLocation.macroRegionId > 0;

    if (!isDrawnAreaType && !isSelectedAreaType && !isPointType) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorAddLocationData"
        )
      );
      return;
    }

    try {
      this.setState(StoreState.PEDDING);
      const operationRuleLocationInserted =
        await operationRuleLocationService.createOperationRuleLocation(
          this.operationRuleLocation.name,
          this.operationRuleLocation.description || "",
          this.operationRuleLocation.regionId,
          this.operationRuleLocation.stationId,
          this.operationRuleLocation.operationRuleId,
          this.operationRuleLocation.macroRegionId,
          this.operationRuleLocation.locationId,
          this.operationRuleLocation.geofence
        );
      if (operationRuleLocationInserted) {
        this.operationRuleLocations.unshift(operationRuleLocationInserted);
        this.setOperationRuleLocationMainId(operationRuleLocationInserted.id);
        this.setState(StoreState.DONE);
      } else {
        this.setState(StoreState.ERROR);
        this.setMessageError(
          i18n.t(
            "oceanWeatherIntelligence.operationRuleLocation.errorAddLocationData"
          )
        );
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorAddLocationData"
        )
      );
    }
  }

  async updateOperationRuleLocation() {
    if (!this.operationRuleLocation) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorUpdateLocationData"
        )
      );
      return;
    }

    const isDrawnAreaType = this.operationRuleLocation.geofence && this.operationRuleLocation.geofence.length > 0;
    const isSelectedAreaType = this.operationRuleLocation.locationId && this.operationRuleLocation.locationId > 0;
    const isPointType = this.operationRuleLocation.macroRegionId && this.operationRuleLocation.macroRegionId > 0;

    if (!isDrawnAreaType && !isSelectedAreaType && !isPointType) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.operationRuleLocation.errorUpdateLocationData"
        )
      );
      return;
    }

    const { operationRuleLocationService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await operationRuleLocationService.updateOperationRuleLocation(
        this.operationRuleLocation.id,
        this.operationRuleLocation.name,
        this.operationRuleLocation.description || "",
        this.operationRuleLocation.regionId,
        this.operationRuleLocation.stationId,
        this.operationRuleLocation.macroRegionId,
        this.operationRuleLocation.locationId,
        this.operationRuleLocation.geofence,
        this.operationRuleLocation.operationRuleId
      );

      const operationRuleLocationIndex = this.operationRuleLocations.indexOf(
        this.getOperationRuleLocation(this.operationRuleLocation.id)
      );
      this.operationRuleLocations[operationRuleLocationIndex] = {
        ...this.operationRuleLocation,
      };
      this.setState(StoreState.DONE);
    } catch (error: any) {
      this.setState(StoreState.ERROR);

      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t(
            "oceanWeatherIntelligence.operationRuleLocation.errorUpdateLocationData"
          )
        );
      }
    }
  }

  async deleteOperationRuleLocation(id: number): Promise<Boolean> {
    if (!id) {
      throw new Error("Need to inform ID for delete data");
    }

    const { operationRuleLocationService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const result =
        await operationRuleLocationService.deleteOperationRuleLocation(id);
      this.setState(StoreState.DONE);
      if (result) {
        // Remove from local store if it exists there
        const operationRuleLocationToRemove = this.getOperationRuleLocation(id);
        if (operationRuleLocationToRemove) {
          const operationRuleLocationToRemoveIndex =
            this.operationRuleLocations.indexOf(operationRuleLocationToRemove);
          this.operationRuleLocations.splice(
            operationRuleLocationToRemoveIndex,
            1
          );
        }
        return true;
      }
    } catch (error: any) {
      this.setState(StoreState.ERROR);

      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t(
            "oceanWeatherIntelligence.operationRuleLocation.errorDeleteLocationData"
          )
        );
      }
    }
    return false;
  }

  async updateOperationRuleLocationOrder({ dashboardId, idsInOrder }: IUpdateOperationRuleLocationOrderParams) {
    const { operationRuleLocationService } = getEnv<RootEnv>();

    try {
      this.setState(StoreState.PEDDING);
      await operationRuleLocationService.updateOperationRuleLocationOrder({ dashboardId, idsInOrder });

      this.setState(StoreState.DONE);
    } catch (error: any) {
      this.setState(StoreState.ERROR);

      if (error?.response?.status === 403) {
        this.setMessageError(i18n.t("oceanWeatherIntelligence.errorSortListPermission"));
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.errorWhileSaveSort")
        );
      }
    }
  }

  async updateOperationRuleLocationById(
    id: number,
    name: string,
    description: string,
    regionId?: number,
    stationId?: number,
    macroRegionId?: number,
    locationId?: number,
    geofence?: { lat: number; lng: number }[],
    operationRuleId?: number
  ): Promise<boolean> {
    const { operationRuleLocationService } = getEnv<RootEnv>();

    try {
      this.setState(StoreState.PEDDING);
      await operationRuleLocationService.updateOperationRuleLocation(
        id,
        name,
        description,
        regionId,
        stationId,
        macroRegionId,
        locationId,
        geofence,
        operationRuleId
      );

      this.setState(StoreState.DONE);
      return true;
    } catch (error: any) {
      this.setState(StoreState.ERROR);

      if (error?.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t(
            "oceanWeatherIntelligence.operationRuleLocation.errorUpdateLocationData"
          )
        );
      }
      return false;
    }
  }
}
